import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, EyeOffIcon, SaveIcon, TimerIcon, XIcon } from 'lucide-react';
import { PasoOrigen } from './PasoOrigen';
import { MiniaturaDocumento } from './MiniaturaDocumento';
import { useDatos } from '../../contexts/DatosContext';
import { CATEGORIAS_DOCUMENTO, etiquetaAtributo } from '../../data/tiposDocumento';
import { abogadoPorId } from '../../data/abogados';
import type { AtributoDocumento, Clasificacion, Documento, Expediente, OrigenArchivo, PaginaDocumento } from '../../types';
import { fechaCorta, fechaRel } from '../../utils/fechas';
import { formatoEvidencia, siguienteEvidencia } from '../../utils/documentos';
const claseInput = 'h-9 w-full rounded border border-gris-200 px-3 text-sm text-gris-800 placeholder:text-gris-400 focus:border-marino-500';
const claseEtiqueta = 'block text-2xs font-semibold uppercase tracking-wide text-gris-500';
const PASOS = ['Origen del archivo', 'Clasificar', 'Confirmar y guardar'];
export function AsistenteEvidencia({
  expediente,
  onCerrar
}: {
  expediente: Expediente;
  onCerrar: () => void;
}) {
  const {
    tiposParaMateria,
    agregarDocumento,
    documentosDeExpediente,
    etapasDe,
    usuarioActual
  } = useDatos();
  const tipos = useMemo(() => tiposParaMateria(expediente.materia), [tiposParaMateria, expediente.materia]);
  const etapas = etapasDe(expediente.materia);
  const numeroEvidencia = siguienteEvidencia(documentosDeExpediente(expediente.id));
  const [paso, setPaso] = useState(1);
  const [origen, setOrigen] = useState<OrigenArchivo>('Archivo');
  const [paginas, setPaginas] = useState<PaginaDocumento[]>([]);
  const [tipoId, setTipoId] = useState('');
  const [etapa, setEtapa] = useState(etapas[expediente.etapaActual]?.nombre ?? '');
  const [descripcion, setDescripcion] = useState('');
  const [fecha, setFecha] = useState(fechaRel(0));
  const [fechaAcuse, setFechaAcuse] = useState('');
  const [fojas, setFojas] = useState('');
  const [autoridadEmisora, setAutoridadEmisora] = useState(expediente.autoridad);
  const [clasificacion, setClasificacion] = useState<Clasificacion>('Público');
  const [vigencia, setVigencia] = useState('');
  const [firmaCedula, setFirmaCedula] = useState(abogadoPorId(expediente.abogadoIds[0])?.cedula ?? '');
  const [cadenaCustodia, setCadenaCustodia] = useState('');
  const [fechaVencimiento, setFechaVencimiento] = useState('');
  const [error, setError] = useState('');
  const tipo = tipos.find(t => t.id === tipoId);
  const exige = (clave: AtributoDocumento) => !!tipo?.atributos.includes(clave);
  const faltantes = () => {
    const lista: string[] = [];
    if (!tipo) lista.push('Tipo de documento');
    if (!descripcion.trim()) lista.push('Descripción');
    if (!etapa) lista.push('Etapa del proceso');
    if (exige('fechaAcuse') && !fechaAcuse) lista.push(etiquetaAtributo('fechaAcuse'));
    if (exige('fojas') && !fojas) lista.push(etiquetaAtributo('fojas'));
    if (exige('autoridadEmisora') && !autoridadEmisora.trim()) lista.push(etiquetaAtributo('autoridadEmisora'));
    if (exige('vigencia') && !vigencia) lista.push(etiquetaAtributo('vigencia'));
    if (exige('firmaCedula') && !firmaCedula.trim()) lista.push(etiquetaAtributo('firmaCedula'));
    if (exige('cadenaCustodia') && !cadenaCustodia.trim()) lista.push(etiquetaAtributo('cadenaCustodia'));
    if (tipo?.requiereVencimiento && !fechaVencimiento) lista.push('Fecha de vencimiento');
    return lista;
  };
  const continuar = () => {
    setError('');
    if (paso === 1) {
      if (paginas.length === 0) {
        setError('Agrega al menos una página: sube un archivo, toma una foto o escanea.');
        return;
      }
      setPaso(2);
      return;
    }
    if (paso === 2) {
      const pendientes = faltantes();
      if (pendientes.length > 0) {
        setError(`Este tipo de documento exige: ${pendientes.join(', ')}.`);
        return;
      }
      setPaso(3);
    }
  };
  const guardar = () => {
    if (!tipo) return;
    const ahora = new Date();
    const fechaHora = `${ahora.toISOString().slice(0, 10)} ${ahora.toTimeString().slice(0, 5)}`;
    const nombreArchivo = `${expediente.localizador}-EV${String(numeroEvidencia).padStart(3, '0')}.${origen === 'Cámara' ? 'jpg' : 'pdf'}`;
    const documento: Documento = {
      id: `doc-${Date.now()}`,
      expedienteId: expediente.id,
      tipoId: tipo.id,
      tipo: tipo.nombre,
      descripcion: descripcion.trim(),
      fecha,
      fechaAcuse: exige('fechaAcuse') ? fechaAcuse : undefined,
      fojas: exige('fojas') ? Number(fojas) : paginas.length,
      clasificacion: exige('clasificacion') ? clasificacion : 'Público',
      archivo: nombreArchivo,
      ubicacion: `${expediente.ubicacion.caja} · Legajo 1`,
      autoridadEmisora: exige('autoridadEmisora') ? autoridadEmisora.trim() : undefined,
      vigencia: exige('vigencia') ? vigencia : undefined,
      firmaCedula: exige('firmaCedula') ? firmaCedula.trim() : undefined,
      cadenaCustodia: exige('cadenaCustodia') ? cadenaCustodia.trim() : undefined,
      fechaVencimiento: tipo.requiereVencimiento ? fechaVencimiento : undefined,
      confidencial: tipo.confidencial,
      esEvidencia: true,
      numeroEvidencia,
      etapa,
      origen,
      paginas,
      subidoPor: usuarioActual,
      subidoEn: fechaHora,
      versiones: [{
        version: 1,
        archivo: nombreArchivo,
        usuario: usuarioActual,
        fechaHora,
        nota: `Digitalización inicial (${origen.toLowerCase()}), ${paginas.length} página(s).`
      }]
    };
    agregarDocumento(documento);
    onCerrar();
  };
  return <section className="border-b border-marino-200 bg-marino-50 px-5 py-5" aria-label="Asistente de carga de evidencias" data-id="element-192" data-mp="2xoi67jgc1ack">
      <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-193" data-mp="2jqbopdvoxmz2">
        <div data-id="element-194" data-mp="2nptypelz4dn4">
          <h3 className="text-sm font-semibold text-marino-900" data-id="element-195" data-mp="2nqlxszes4ti5">
            Digitalizar documento ·{' '}
            <span className="font-mono" data-id="element-196" data-mp="u9pnin9qyg42">{formatoEvidencia(numeroEvidencia)}</span>
          </h3>
          <p className="mt-0.5 text-xs text-gris-500" data-id="element-197" data-mp="pgda79aipvqm">
            Expediente <span className="font-mono text-marino-700" data-id="element-198" data-mp="khtkcxsszzsz">{expediente.localizador}</span> ·
            carga a nombre de {usuarioActual}
          </p>
        </div>
        <button type="button" onClick={onCerrar} aria-label="Cerrar asistente" className="flex h-8 w-8 items-center justify-center rounded border border-gris-200 bg-white text-gris-500 transition-colors duration-150 hover:border-gris-300" data-id="element-199" data-mp="1d78xpu7xy4s2">
          <XIcon className="h-4 w-4" data-id="element-200" data-mp="13axj2xkvp2ug" />
        </button>
      </div>

      <ol className="mt-4 flex flex-wrap items-center gap-3" data-id="element-201" data-mp="z6y62oyt4m5u">
        {PASOS.map((nombre, i) => {
        const numero = i + 1;
        const activo = paso === numero;
        const hecho = paso > numero;
        return <li key={nombre} className="flex items-center gap-2" data-id="element-202" data-mp="3k031k98u1p22" data-mpi={i}>
              <span className={`flex h-6 w-6 items-center justify-center rounded-full text-2xs font-bold ${hecho ? 'bg-marino-700 text-white' : activo ? 'bg-ambar-400 text-marino-900' : 'border border-gris-300 bg-white text-gris-400'}`} data-id="element-203" data-mp="1zh902ytrz8lt">
                {hecho ? <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-204" data-mp="2i5npxiscwgl1" /> : numero}
              </span>
              <span className={`text-xs ${activo ? 'font-semibold text-marino-900' : 'text-gris-500'}`} data-id="element-205" data-mp="2n3p9e4tp2hx1">
                {nombre}
              </span>
              {i < PASOS.length - 1 && <span className="h-px w-6 bg-gris-300 sm:w-10" aria-hidden="true" data-id="element-206" data-mp="2w3kogkqdex2c" />}
            </li>;
      })}
      </ol>

      <div className="mt-5 rounded-lg border border-gris-200 bg-white px-5 py-5" data-id="element-207" data-mp="11r4bz26zemp1">
        {paso === 1 && <PasoOrigen origen={origen} setOrigen={setOrigen} paginas={paginas} setPaginas={setPaginas} data-id="element-208" data-mp="33oejtf74l6dn" />}

        {paso === 2 && <div className="space-y-4" data-id="element-209" data-mp="3de7v0f3cvjhd">
            <div className="grid gap-4 md:grid-cols-3" data-id="element-210" data-mp="3dhdkvwjt6pe5">
              <label data-id="element-211" data-mp="38h7m7wf4g2mk">
                <span className={claseEtiqueta} data-id="element-212" data-mp="38gy6d1nsjbul">Tipo de documento *</span>
                <select value={tipoId} onChange={e => setTipoId(e.target.value)} className={`mt-1.5 ${claseInput}`} data-id="element-213" data-mp="2krmzd2fukl80">
                  <option value="" data-id="element-214" data-mp="2up1ft21rta0b">Selecciona del catálogo</option>
                  {CATEGORIAS_DOCUMENTO.map(categoria => {
                const delGrupo = tipos.filter(t => t.categoria === categoria);
                if (delGrupo.length === 0) return null;
                return <optgroup key={categoria} label={categoria} data-id="element-215" data-mp="4yebrshn3twm">
                        {delGrupo.map(t => <option key={t.id} value={t.id} data-id="element-216" data-mp="1mvj2ciuwr7f4">
                            {t.nombre}
                          </option>)}
                      </optgroup>;
              })}
                </select>
              </label>

              <label data-id="element-217" data-mp="1iuphjycri8qg">
                <span className={claseEtiqueta} data-id="element-218" data-mp="1iw0tilgd2msn">Etapa del proceso *</span>
                <select value={etapa} onChange={e => setEtapa(e.target.value)} className={`mt-1.5 ${claseInput}`} data-id="element-219" data-mp="26kti1wunlsd8">
                  <option value="" data-id="element-220" data-mp="1wvs4edbkzn2f">Selecciona la etapa</option>
                  {etapas.map((et, i) => <option key={et.id} value={et.nombre} data-id="element-221" data-mp="21tjt6ygxit2e" data-mpi={i}>
                      {i + 1}. {et.nombre}
                      {i === expediente.etapaActual ? ' (actual)' : ''}
                    </option>)}
                </select>
              </label>

              <label data-id="element-222" data-mp="1gixbzjwqp1jc">
                <span className={claseEtiqueta} data-id="element-223" data-mp="1ginmryjiqyep">Etiqueta de evidencia</span>
                <input readOnly value={formatoEvidencia(numeroEvidencia)} className={`mt-1.5 ${claseInput} bg-gris-50 font-mono text-marino-800`} data-id="element-224" data-mp="1li05q879rlc0" />
                <span className="mt-1 block text-2xs text-gris-400" data-id="element-225" data-mp="1bjayragwvlaj">
                  Consecutivo automático dentro del expediente.
                </span>
              </label>

              <label className="md:col-span-3" data-id="element-226" data-mp="12j5u3833r102">
                <span className={claseEtiqueta} data-id="element-227" data-mp="12jxoifkyqsor">Descripción *</span>
                <input value={descripcion} onChange={e => setDescripcion(e.target.value)} className={`mt-1.5 ${claseInput}`} placeholder="Ej. Estados de cuenta bancarios del periodo investigado" data-id="element-228" data-mp="nurr8x5orcji" />
              </label>
            </div>

            {tipo && <>
                <div className="flex flex-wrap items-center gap-2" data-id="element-229" data-mp="juhacpgcv1pv">
                  {tipo.atributos.map(a => <span key={a} className="rounded bg-marino-50 px-2 py-0.5 text-2xs font-medium text-marino-700 ring-1 ring-marino-100" data-id="element-230" data-mp="3rdfkvy2vx8gl">
                      Exige: {etiquetaAtributo(a)}
                    </span>)}
                  {tipo.requiereVencimiento && <span className="inline-flex items-center gap-1.5 rounded bg-ambar-50 px-2 py-0.5 text-2xs font-semibold text-ambar-700 ring-1 ring-ambar-100" data-id="element-231" data-mp="j45sbwqn0pxy">
                      <TimerIcon className="h-3 w-3" aria-hidden="true" data-id="element-232" data-mp="svtizvfde2yd" />
                      Requiere fecha de vencimiento
                    </span>}
                  {tipo.confidencial && <span className="inline-flex items-center gap-1.5 rounded bg-marino-800 px-2 py-0.5 text-2xs font-semibold text-white" data-id="element-233" data-mp="11x01ga1kxnu9">
                      <EyeOffIcon className="h-3 w-3" aria-hidden="true" data-id="element-234" data-mp="wvamijb8kq0w" />
                      Confidencial
                    </span>}
                </div>

                <div className="grid gap-4 border-t border-gris-100 pt-4 md:grid-cols-3" data-id="element-235" data-mp="3vog6r4ydl3un">
                  <label data-id="element-236" data-mp="3iuikroo5fdd">
                    <span className={claseEtiqueta} data-id="element-237" data-mp="3ikpgv8bus3g">Fecha del documento *</span>
                    <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} className={`tabular mt-1.5 ${claseInput}`} data-id="element-238" data-mp="8cf5nooq4oxs" />
                  </label>

                  {exige('fechaAcuse') && <label data-id="element-239" data-mp="n6k3cey6ufd3">
                      <span className={claseEtiqueta} data-id="element-240" data-mp="rai04j01je2z">{etiquetaAtributo('fechaAcuse')} *</span>
                      <input type="date" value={fechaAcuse} onChange={e => setFechaAcuse(e.target.value)} className={`tabular mt-1.5 ${claseInput}`} data-id="element-241" data-mp="2p09kd0j8g3ea" />
                    </label>}

                  {exige('fojas') && <label data-id="element-242" data-mp="2gp0trbosg95i">
                      <span className={claseEtiqueta} data-id="element-243" data-mp="2brr8874ishmy">{etiquetaAtributo('fojas')} *</span>
                      <input type="number" min={1} value={fojas} onChange={e => setFojas(e.target.value)} className={`tabular mt-1.5 ${claseInput}`} placeholder={`${paginas.length} páginas digitalizadas`} data-id="element-244" data-mp="27qffhsqcngaj" />
                    </label>}

                  {exige('autoridadEmisora') && <label className="md:col-span-2" data-id="element-245" data-mp="2qgnfw32nzsn1">
                      <span className={claseEtiqueta} data-id="element-246" data-mp="1k14lqv7f2m5w">
                        {etiquetaAtributo('autoridadEmisora')} *
                      </span>
                      <input value={autoridadEmisora} onChange={e => setAutoridadEmisora(e.target.value)} className={`mt-1.5 ${claseInput}`} data-id="element-247" data-mp="1f3unqr5yp26g" />
                    </label>}

                  {exige('clasificacion') && <label data-id="element-248" data-mp="2xz3cd5owmpf6">
                      <span className={claseEtiqueta} data-id="element-249" data-mp="2y0efr4eftv5r">
                        Público / Privado / Copia certificada *
                      </span>
                      <select value={clasificacion} onChange={e => setClasificacion(e.target.value as Clasificacion)} className={`mt-1.5 ${claseInput}`} data-id="element-250" data-mp="3ln4kidi683u2">
                        <option value="Público" data-id="element-251" data-mp="3hm133mkh5zqq">Público</option>
                        <option value="Privado" data-id="element-252" data-mp="219dl8afqgpqo">Privado</option>
                        <option value="Copia certificada" data-id="element-253" data-mp="21b6vs0tgvu3y">Copia certificada</option>
                      </select>
                    </label>}

                  {exige('vigencia') && <label data-id="element-254" data-mp="1xt9ecjrb4sq9">
                      <span className={claseEtiqueta} data-id="element-255" data-mp="1tpuvzmfwc09n">{etiquetaAtributo('vigencia')} *</span>
                      <input type="date" value={vigencia} onChange={e => setVigencia(e.target.value)} className={`tabular mt-1.5 ${claseInput}`} data-id="element-256" data-mp="27p421tae1dzw" />
                    </label>}

                  {exige('firmaCedula') && <label data-id="element-257" data-mp="1a3f489ydhi9p">
                      <span className={claseEtiqueta} data-id="element-258" data-mp="1oqhze9z83etv">{etiquetaAtributo('firmaCedula')} *</span>
                      <select value={firmaCedula} onChange={e => setFirmaCedula(e.target.value)} className={`mt-1.5 ${claseInput}`} data-id="element-259" data-mp="1osb61p9u5z1v">
                        {expediente.abogadoIds.map(id => {
                  const a = abogadoPorId(id);
                  if (!a) return null;
                  return <option key={id} value={a.cedula} data-id="element-260" data-mp="2wbnbksr8jdn0">
                              {a.nombre} — cédula {a.cedula}
                            </option>;
                })}
                      </select>
                    </label>}

                  {exige('cadenaCustodia') && <label data-id="element-261" data-mp="28gc8aogntwt8">
                      <span className={claseEtiqueta} data-id="element-262" data-mp="2ddkseuldcax4">{etiquetaAtributo('cadenaCustodia')} *</span>
                      <input value={cadenaCustodia} onChange={e => setCadenaCustodia(e.target.value)} className={`mt-1.5 ${claseInput} font-mono`} placeholder="Ej. CC-2026-0184" data-id="element-263" data-mp="2hf61e4us8521" />
                    </label>}

                  {tipo.requiereVencimiento && <label data-id="element-264" data-mp="bryws2afpm3x">
                      <span className={claseEtiqueta} data-id="element-265" data-mp="bs8buutxy8uu">Fecha de vencimiento *</span>
                      <input type="date" value={fechaVencimiento} onChange={e => setFechaVencimiento(e.target.value)} className={`tabular mt-1.5 ${claseInput}`} data-id="element-266" data-mp="6t5ja9v5dv3r" />
                    </label>}
                </div>
              </>}
          </div>}

        {paso === 3 && <div className="grid gap-6 lg:grid-cols-[auto_1fr]" data-id="element-267" data-mp="3k6qn15f6i4io">
            <div data-id="element-268" data-mp="3f8ffx2g9q845">
              <p className={claseEtiqueta} data-id="element-269" data-mp="3f7xapgbmojk3">Documento digitalizado</p>
              <div className="mt-2 flex flex-wrap gap-2" data-id="element-270" data-mp="3b73msmy6r4r7">
                {paginas.slice(0, 4).map((p, i) => <MiniaturaDocumento key={p.id} semilla={p.id} origen={p.origen} rotacion={p.rotacion} url={p.url} ancho={64} alto={84} etiqueta={`Página ${i + 1}`} data-id="element-271" data-mp="1dh2beei7q28m" data-mpi={i} />)}
                {paginas.length > 4 && <span className="flex h-[84px] w-16 items-center justify-center rounded border border-dashed border-gris-300 text-2xs text-gris-500" data-id="element-272" data-mp="1iz77vrmm4ttm">
                    +{paginas.length - 4}
                  </span>}
              </div>
              <p className="tabular mt-2 text-2xs text-gris-500" data-id="element-273" data-mp="192m0izn4f6mm">
                {paginas.length} páginas · origen {origen.toLowerCase()}
              </p>
            </div>

            <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2" data-id="element-274" data-mp="1014l7jhjf8yz">
              {[['Evidencia', formatoEvidencia(numeroEvidencia)], ['Tipo de documento', tipo?.nombre ?? '—'], ['Etapa del proceso', etapa], ['Descripción', descripcion], ['Fecha del documento', fechaCorta(fecha)], ...(fechaAcuse ? [['Acuse / presentación', fechaCorta(fechaAcuse)]] : []), ...(exige('fojas') ? [['Fojas', fojas]] : [['Fojas', String(paginas.length)]]), ...(exige('clasificacion') ? [['Clasificación', clasificacion]] : []), ...(autoridadEmisora && exige('autoridadEmisora') ? [['Autoridad emisora', autoridadEmisora]] : []), ...(vigencia ? [['Vigencia', fechaCorta(vigencia)]] : []), ...(firmaCedula && exige('firmaCedula') ? [['Cédula', firmaCedula]] : []), ...(cadenaCustodia ? [['Cadena de custodia', cadenaCustodia]] : []), ...(fechaVencimiento ? [['Vencimiento', fechaCorta(fechaVencimiento)]] : []), ['Carga registrada por', usuarioActual]].map(([etiquetaCampo, valor]) => <div key={etiquetaCampo as string} data-id="element-275" data-mp="26vtyfrkh7pph">
                  <dt className="text-2xs font-semibold uppercase tracking-wide text-gris-400" data-id="element-276" data-mp="22tqml1twaj3o">
                    {etiquetaCampo}
                  </dt>
                  <dd className="mt-0.5 text-sm text-gris-800" data-id="element-277" data-mp="157dslwp3jdmf">{valor || '—'}</dd>
                </div>)}
            </dl>
          </div>}
      </div>

      {error && <p role="alert" className="mt-3 text-xs font-medium text-peligro-600" data-id="element-278" data-mp="1ex70onq15alx">
          {error}
        </p>}

      <div className="mt-4 flex items-center justify-between" data-id="element-279" data-mp="r7cs4icqu87h">
        <button type="button" onClick={() => paso === 1 ? onCerrar() : setPaso(paso - 1)} className="inline-flex h-9 items-center gap-2 rounded border border-gris-200 bg-white px-3 text-sm font-medium text-gris-600 transition-colors duration-150 hover:border-gris-300" data-id="element-280" data-mp="r9p5ei7k3ue7">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-281" data-mp="w920trchu9se" />
          {paso === 1 ? 'Cancelar' : 'Atrás'}
        </button>

        {paso < 3 ? <button type="button" onClick={continuar} className="inline-flex h-9 items-center gap-2 rounded bg-marino-800 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-282" data-mp="27l2tvige4kdy">
            Continuar
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-283" data-mp="257zd2nsk8nd0" />
          </button> : <button type="button" onClick={guardar} className="inline-flex h-9 items-center gap-2 rounded bg-marino-800 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-284" data-mp="2a5hdtqwfbw9x">
            <SaveIcon className="h-4 w-4" aria-hidden="true" data-id="element-285" data-mp="32wsm3oggkf3d" />
            Guardar evidencia
          </button>}
      </div>
    </section>;
}